// Import JS Modules
import { builderDispatch, WebUI, PrismUI } from '../../_utils/ui';
import get from 'lodash/get';
import remove from 'lodash/remove';
import objectToFormData from '../../_utils/object-to-form-data-util';
import PageSpreads from '../edit-area/page-spreads';
import Utils from './utils';
import { usePrismItemStore } from 'stores/usePrismItemStore';
import { StickerStore, PatternStore } from 'stores/embellishments';
import { EventEmitter } from 'wk-event-emitter';

// --------------------------------------------------------------------

$.mobile = {
  loading() {},
};
$.fn.listview = function () {};

const RETRY_PERIOD = 1000;
const MAX_RETRIALS = 20;

function loadPrismItem(vars, options = {}, skipAddToStack = false) {
  const { initialPage } = options;
  builderDispatch.dispatch({
    type: PrismUI.ITEM_COPYING,
  });

  if (!vars.hasOwnProperty('id')) {
    return Promise.reject();
  }

  const dateIdentifier = Date.now();
  if (skipAddToStack && activeView) {
    activeView.historyStack.skipStep(dateIdentifier);
  }

  Utils.load();
  usePrismItemStore
    .getState()
    .refreshImages()
    .catch(() => {});
  usePrismItemStore.getState().updateId(vars.id);
  usePrismItemStore.getState().updateLoadActive(true);

  return new Promise((resolve, reject) => {
    perform({
      url: Utils.dataURL(vars),
      resolve,
      reject,
    });
  }).finally(() => {
    builderDispatch.dispatch({
      type: PrismUI.ITEM_COPYING_FINISHED,
    });
  });

  function perform({ url, resolve, reject, attempt = 0 }) {
    fetch(url, {
      credentials: 'same-origin',
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }

        throw new Error(response.statusText);
      })
      .then((data) => {
        if (data.copy && data.copy.status) {
          switch (data.copy.status) {
            case 'queued':
            case 'working':
              builderDispatch.dispatch({
                type: PrismUI.ITEM_COPYING,
                data: data.copy,
              });

              if (attempt < MAX_RETRIALS) {
                setTimeout(() => {
                  perform({
                    url,
                    resolve,
                    reject,
                    attempt: ++attempt,
                  });
                }, RETRY_PERIOD);
              } else {
                reject('Transformation timed out');
              }
              break;
            case 'completed':
            case undefined: // This is the default returned value upon completion!
              resolve();
              break;
            default:
              reject('Transformation failure');
              break;
          }
        } else {
          if (initialPage) data.currentPage = initialPage;
          builderDispatch.dispatch({
            type: PrismUI.DO_ITEM_LOAD,
            data,
            dateIdentifier,
          });

          resolve();
        }
      })
      .catch((error) => {
        usePrismItemStore.getState().updateLoadActive(false);
        reject(error.message);
      });
  }
}

// Details
try {
  window.prismViews = window.prismViews || {};
} catch (e) {}

const PrismInfo = {
  get prismItem() {
    return usePrismItemStore.getState().prismItem;
  },
  get pages() {
    return usePrismItemStore.getState().prismItem.pages;
  },
  get sections() {
    return usePrismItemStore.getState().prismItem.product.sections;
  },

  hasDoubleSpread() {
    for (const key in this.pages) {
      if (typeof this.pages[key]['page-pair'] === 'number') return true;
    }

    return false;
  },

  canAddPages() {
    return usePrismItemStore.getState().maxPagesReached();
  },

  canReorder() {
    return this.sections.some((section) => section.max > 1);
  },

  hasFirstPage() {
    const tags = get(this.sections[0], 'tags', []);

    return tags.includes('first-page');
  },
};

const prismAction = {
  notifyableOperations: ['autofill', 'set_theme', 'add_page', 'delete_page'],
  updatePages({ pages, pageCount, currentPage }) {
    if (!pages) {
      return;
    }

    usePrismItemStore.getState().updatePrismItem({ pages, pageCount });

    builderDispatch.dispatch({
      type: WebUI.PAGES_CHANGED,
      detail: 'prismAction.updatePages handler',
    });

    if (currentPage > -1) {
      const index = (window.activeView && window.activeView.pageIndex) || 0;
      builderDispatch.delayedDispatch({
        type: WebUI.DO_SHOW_SPREAD,
        spread: new PageSpreads(usePrismItemStore.getState().prismItem).find(
          index,
        ),
      });

      // delayedDispatch here because HIDE_CUSTOM_PAGE listeners (chiclets-base) call
      // handleItemClick which dispatches again — synchronous dispatch would throw
      // "Cannot dispatch in the middle of a dispatch".
      builderDispatch.delayedDispatch({
        type: WebUI.HIDE_CUSTOM_PAGE,
      });
    }
  },
  deleteImage(imageId) {
    const pageGetPromises = [];
    const masterDeferred = $.Deferred();

    for (let i = 0; i < usePrismItemStore.getState().prismItem.pageCount; i++) {
      const isActivePage = window.activeView && activeView.pageIndex == i;
      const deferred = $.Deferred();
      pageGetPromises.push(deferred.promise());

      if (isActivePage) {
        // For the active page, let the canvas handle the slot cleanup
        if (!activeView.hasImage(imageId)) {
          deferred.resolve();
          continue;
        }
        activeView.deleteImage(imageId, { skipSave: true });

        const pageData = activeView.getData();
        usePrismItemStore
          .getState()
          .savePage(i, pageData)
          .then(() => {
            deferred.resolve();
          });
      } else {
        let removed;
        usePrismItemStore.getState().getPage(i, (page) => {
          const { slots } = page;
          removed = remove(slots, (slot) => slot.data.imgId == imageId);

          if (removed.length < 1) {
            deferred.resolve();
            return;
          }
          page.slots = slots;
          usePrismItemStore
            .getState()
            .savePage(i, page)
            .then(() => {
              deferred.resolve();
            });

          EventEmitter.emit(PrismUI.PAGE_CHANGED_OUTSIDE_EDITOR, { page });
        });
      }
    }

    $.when.apply(null, pageGetPromises).then(() => {
      masterDeferred.resolve();
    });

    return masterDeferred.promise();
  },
  post(operation, url, data, callback) {
    const shouldNotify = this.notifyableOperations.includes(operation);

    if (shouldNotify) {
      builderDispatch.delayedDispatch({
        type: PrismUI.MODAL_MESSAGE,
      });
    }

    fetch(url, {
      method: 'POST',
      body: objectToFormData(data),
      credentials: 'same-origin',
    }).then((response) =>
      this.handleResponse(response, shouldNotify, callback),
    );
  },
  postJSON(operation, url, data, callback) {
    const shouldNotify = this.notifyableOperations.includes(operation);

    if (shouldNotify) {
      builderDispatch.delayedDispatch({
        type: PrismUI.MODAL_MESSAGE,
      });
    }

    fetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
    }).then((response) =>
      this.handleResponse(response, shouldNotify, callback),
    );
  },
  async handleResponse(response, shouldNotify, callback) {
    if (!response.ok) {
      throw Error(response.statusText);
    }

    try {
      const data = await response.json();

      this.updatePages(data);

      if (shouldNotify) {
        builderDispatch.delayedDispatch({
          type: PrismUI.MODAL_MESSAGE,
          action: 'hide',
        });
      }

      if (typeof callback === 'function') {
        callback(data);
      }
    } catch (error) {
      console.error(error);
    }
  },
  autofill(payload, callback) {
    this.postJSON(
      'autofill',
      `/m/a/${usePrismItemStore.getState().prismItem.id}/autofill`,
      payload,
      callback,
    );
  },
  autoclear(payload, callback) {
    this.postJSON(
      'autoclear',
      `/m/a/${usePrismItemStore.getState().prismItem.id}/autoclear`,
      payload,
      callback,
    );
  },
  clear(callback) {
    this.post(
      'clear',
      `/m/a/${usePrismItemStore.getState().prismItem.id}/clear`,
      {},
      callback,
    );
  },

  add_page(position, callback, index) {
    if (typeof index === 'undefined') {
      const isLeftSide = (({ pageIndex }) =>
        // some covers are really a first page
        // and that knocks the pages' indexing off by one
        PrismInfo.hasFirstPage() ? pageIndex % 2 !== 0 : pageIndex % 2 === 0)(
        activeView,
      );

      position =
        {
          left: 'before',
          right: 'after',
        }[position] || (isLeftSide ? 'before' : 'after');

      index = (({ pageIndex }) => {
        if (usePrismItemStore.getState().isLayflat()) {
          return pageIndex;
        }

        if (position === 'before') {
          pageIndex -= isLeftSide ? 0 : 1;
        }

        if (position === 'after') {
          pageIndex += isLeftSide ? 1 : 0;
        }

        return pageIndex;
      })(activeView);
    }

    this.post(
      'add_page',
      `/m/a/${usePrismItemStore.getState().prismItem.id}/add_pages`,
      { index, position },
      callback,
    );
  },

  delete_page(callback, pageIndex = null) {
    const index = (({ pageIndex: pageIndexActiveView }) => {
      pageIndex = pageIndex || pageIndexActiveView;
      if (usePrismItemStore.getState().isLayflat()) {
        return pageIndex;
      }

      // some covers are really a first page
      // and that knocks the pages' indexing off by one
      const isLeftSide = PrismInfo.hasFirstPage()
        ? pageIndex % 2 !== 0
        : pageIndex % 2 === 0;

      return pageIndex - (isLeftSide ? 0 : 1);
    })(activeView);

    this.post(
      'delete_page',
      `/m/a/${usePrismItemStore.getState().prismItem.id}/delete_pages`,
      { index },
      callback,
    );
  },
  set_theme(theme, callback) {
    this.post(
      'write',
      `/project_edit/write/${usePrismItemStore.getState().prismItem.id}`,
      { theme },
      () => {
        this.post(
          'set_theme',
          `/m/a/${usePrismItemStore.getState().prismItem.id}/set_theme`,
          { theme, index: usePrismItemStore.getState().prismItem.currentPage },
          (data) => {
            usePrismItemStore.setState(({ prismItem }) => ({
              prismItem: {
                ...prismItem,
                theme: data.theme,
                design_code: data.design_code,
              },
            }));

            if (typeof callback === 'function') {
              callback(data);
            }

            builderDispatch.dispatch({
              type: PrismUI.THEME_CHANGED,
              theme,
            });
          },
        );
      },
    );
  },
  save_wkdata(callback) {
    this.post(
      'save_wkdata',
      wkData.write_url,
      { values: wkData.values },
      callback,
    );
  },
};

const ThumbnailStore = {
  any() {
    return !this.empty();
  },
  empty() {
    return (
      usePrismItemStore.getState().prismItem &&
      (usePrismItemStore.getState().prismItem.imageList || []).length === 0
    );
  },
  list() {
    if (
      usePrismItemStore.getState().prismItem &&
      usePrismItemStore.getState().prismItem.imageList
    ) {
      const usedImageIds = usePrismItemStore
        .getState()
        .usedImages()
        .reduce((memo, usedImageId) => {
          memo[usedImageId] = true;
          return memo;
        }, {});

      return usePrismItemStore.getState().prismItem.imageList.map((image) =>
        Object.assign(image, {
          used: usedImageIds[image.id] === true,
        }),
      );
    }

    return [];
  },
  id(image) {
    if (typeof image === 'number') return image;

    if (image.imgId) {
      return image.imgId;
    }

    return image.id;
  },
  remove(image) {
    const id = this.id(image);

    fetch(
      `/edt/img/del/${usePrismItemStore.getState().prismItem.sec}/${
        usePrismItemStore.getState().prismItem.id
      }/${id}`,
      {
        method: 'POST',
        credentials: 'same-origin',
      },
    )
      .then((response) => {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        // removing image reference from WK
        fetch(
          `/image_references/${
            usePrismItemStore.getState().prismItem.id
          }/${id}`,
          {
            method: 'DELETE',
            credentials: 'same-origin',
          },
        ).catch((e) => console.error(e));

        return response;
      })
      .then(() => {
        const newList = [];

        for (const image of ThumbnailStore.list()) {
          if (ThumbnailStore.id(image) === id) {
            continue;
          }

          newList.push(image);
        }

        usePrismItemStore.getState().updateImageList(newList);
      })
      .catch((e) => console.error(e));
  },
};

const FontStore = {
  fetch() {
    fetch(`/m/a/${usePrismItemStore.getState().prismItem.id}/fonts`, {
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        return response;
      })
      .then((response) => response.json())
      .then((data) => {
        FontStore.list = data;
        builderDispatch.dispatch({ type: PrismUI.FONTS_LOADED });
      })
      .catch((e) => console.error(e));
  },
  list: [],
};

let globalResizeListener;

function didMountResizable(self, delay) {
  if (!globalResizeListener) {
    globalResizeListener = () => {
      window.setTimeout(() => {
        builderDispatch.dispatch({
          type: WebUI.RESIZE,
        });
      }, 200);
    };
    window.addEventListener('resize', globalResizeListener);
  }
  self.resizeHandler = builderDispatch.register((payload) => {
    if (payload.type === WebUI.RESIZE) {
      if (delay) {
        setTimeout(() => {
          self.forceUpdate();
        }, 200);
      } else self.forceUpdate();
    }
  });
  setTimeout(() => {
    self.forceUpdate();
  }, 200);
}

function willUnmountResizable(self) {
  if (self.resizeHandler) {
    builderDispatch.unregister(self.resizeHandler);
  }
  self.resizeHandler = undefined;
  if (globalResizeListener) {
    window.removeEventListener('resize', globalResizeListener);
    globalResizeListener = undefined;
  }
}

function findInArray(array, property, value) {
  for (let i = 0; i < array.length; i++) {
    if (array[i] && array[i][property] && array[i][property] == value) {
      return array[i];
    }
  }
}

export const stickerStore = new StickerStore();
export const patternStore = new PatternStore();

export {
  PrismInfo,
  findInArray,
  willUnmountResizable,
  didMountResizable,
  prismAction,
  ThumbnailStore,
  FontStore,
  loadPrismItem,
};
